import { useEffect, useRef, useState } from "react";
import { Modal } from "@/ui/Modal";
import { PopupSizes } from "@/ui/Modal/types";
import { checkHtmlAddresses, createPage, deletePage, getPage, publishPage, updatePage } from "@/api/pages";
import { getSessionToken } from "@/api/auth";
import { getCampaign } from "@/api/campaigns";
import { DroppedPage, RowState, STEP_PCT, STEP_TEXT, Step, freeSlug, pageIdBySlug, pageTooBig, readDrop, readFiles, titleOnly } from "./dropPublish";

// Sites round 2 (D16) — drop pages (or a whole folder) and they go live in seconds, with a progress
// bar per page and plain-English status. Safety net for client sites: replacing a page that's
// already live asks first, and every batch can be undone for a few seconds afterwards.

type LivePage = { id: string; slug?: string; live: boolean; name?: string };

export const DropToPublish = ({
  campaignId,
  siteAddress,
  pages,
  onChanged,
  onPaste,
}: {
  campaignId: string;
  siteAddress?: string;
  pages: LivePage[]; // this site's pages (for "is the page being replaced live?")
  onChanged: () => void;
  onPaste: () => void;
}) => {
  const [over, setOver] = useState(false);
  const [rows, setRows] = useState<RowState[]>([]);
  const [running, setRunning] = useState(false);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [confirm, setConfirm] = useState<{ text: string; resolve: (ok: boolean) => void } | null>(null);
  const [undo, setUndo] = useState<{ rows: RowState[]; until: number } | null>(null);
  const [undoing, setUndoing] = useState(false);
  const depth = useRef(0);
  const input = useRef<HTMLInputElement>(null);
  const token = getSessionToken() || "";
  const where = (slug: string) => (siteAddress ? `https://${siteAddress}/${slug}` : `/${slug}`);

  const patch = (key: string, p: Partial<RowState>) => setRows((now) => now.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const step = (key: string, s: Step, extra: Partial<RowState> = {}) => patch(key, { step: s, pct: STEP_PCT[s], ...extra });
  const ask = (text: string) => new Promise<boolean>((resolve) => setConfirm({ text, resolve }));

  // the "+ Add pages" buttons open the file picker
  useEffect(() => {
    const open = () => input.current?.click();
    window.addEventListener("sites:browse", open);
    return () => window.removeEventListener("sites:browse", open);
  }, []);

  useEffect(() => {
    if (!undo) return;
    const t = setTimeout(() => setUndo(null), Math.max(0, undo.until - Date.now()));
    return () => clearTimeout(t);
  }, [undo]);

  const isFree = async (slug: string) => {
    const r = await checkHtmlAddresses({ token, campaignId, requests: [{ key: "x", slug }] });
    return r?.ok && r.results?.[0]?.level === "ok";
  };

  const publishAll = async (dropped: DroppedPage[], skippedNames: string[]) => {
    if (!dropped.length) {
      setSkipped(skippedNames);
      return;
    }
    setRunning(true);
    setSkipped(skippedNames);
    setUndo(null);
    const start: RowState[] = dropped.map((d) => ({ key: d.key, filename: d.filename, name: d.filename, slug: "", step: "queued", pct: STEP_PCT.queued }));
    setRows(start);

    // names + addresses from titles and file names, then one check for all of them
    const sugg = await checkHtmlAddresses({ token, campaignId, files: dropped.map((d) => ({ key: d.key, filename: d.filename, html: titleOnly(d.html) })) });
    const planned = dropped.map((d) => {
      const s = (sugg?.suggestions || []).find((x: any) => x.key === d.key) || {};
      return { ...d, name: s.name || d.filename.replace(/\.html?$/i, ""), slug: s.slug || "" };
    });
    const checked = await checkHtmlAddresses({ token, campaignId, requests: planned.map(({ key, slug }) => ({ key, slug })) });
    const results: Record<string, any> = Object.fromEntries((checked?.results || []).map((r: any) => [r.key, r]));
    const usedInBatch = new Set<string>();
    const done: RowState[] = [];

    for (const p of planned) {
      let { slug } = p;
      let note: string | undefined;
      const big = pageTooBig(p.html);
      if (big) { step(p.key, "failed", { name: p.name, note: big }); continue; }
      step(p.key, "checking", { name: p.name, slug });
      const res = results[p.key] || {};
      let replaceId: string | undefined = res.level === "warn" ? res.replacesPageId : undefined;

      // taken elsewhere in the account, or twice in this drop → pick the next free address
      if (res.level === "blocked" || usedInBatch.has(slug)) {
        const free = await freeSlug(slug, async (s) => !usedInBatch.has(s) && (await isFree(s)));
        if (!free) {
          step(p.key, "failed", { note: res.reason || "That address is taken. Rename the file and drop it again." });
          continue;
        }
        note = `/${slug} was taken, so it's at /${free.slug}`;
        slug = free.slug;
        replaceId = undefined;
      }
      usedInBatch.add(slug);

      // replacing a page that's already live → ask first
      let previousHtml: string | undefined;
      if (replaceId) {
        const target = pages.find((x) => x.id === replaceId);
        if (target?.live) {
          step(p.key, "waiting", { slug });
          const ok = await ask(`"${target.name || slug}" is live at ${siteAddress || ""}/${slug}. Replace it with ${p.filename}?`);
          if (!ok) {
            step(p.key, "kept", { slug });
            continue;
          }
        }
        previousHtml = (await getPage(replaceId, { token }))?.data?.htmlContent;
      }

      step(p.key, "saving", { slug, note });
      const saved = replaceId
        ? await updatePage(replaceId, { token, settings: { name: p.name, htmlContent: p.html, fileName: p.filename } })
        : await createPage({ token, settings: { campaignId, name: p.name, slug, type: "html", theme: "html", content: [], htmlContent: p.html, fileName: p.filename } });
      // a new page comes back as the site's canvas object, so look the page up by its address
      const pageId = replaceId || pageIdBySlug(await getCampaign({ token, id: campaignId }), slug);
      if (!saved || saved.error || !pageId) {
        step(p.key, "failed", { note: saved?.error || "Couldn't upload this page. Try again." });
        continue;
      }

      step(p.key, "publishing", { pageId });
      const pub: any = await publishPage(pageId, { token });
      if (!pub || pub.error || pub.ok === false) {
        step(p.key, "failed", { pageId, created: !replaceId, note: `Saved as a draft, but didn't go live: ${pub?.error || "try Publish again"}` });
        continue;
      }
      const row: RowState = { key: p.key, filename: p.filename, name: p.name, slug, step: "live", pct: 100, url: pub.url || where(slug), note: note || pub.note, pageId, created: !replaceId, previousHtml };
      patch(p.key, row);
      done.push(row);
    }

    setRunning(false);
    if (done.length) {
      setUndo({ rows: done, until: Date.now() + 10000 });
      onChanged();
    }
  };

  const undoBatch = async () => {
    if (!undo) return;
    setUndoing(true);
    for (const r of undo.rows) {
      if (r.created && r.pageId) await deletePage(r.pageId, { token }); // takes it offline too
      else if (r.pageId && r.previousHtml !== undefined) {
        await updatePage(r.pageId, { token, settings: { htmlContent: r.previousHtml } });
        await publishPage(r.pageId, { token });
      }
    }
    setUndoing(false);
    setUndo(null);
    setRows([]);
    onChanged();
  };

  const take = async (fn: () => Promise<{ pages: DroppedPage[]; skipped: string[] }>) => {
    if (running) return;
    const { pages: dropped, skipped: sk } = await fn();
    publishAll(dropped, sk);
  };

  // Dragging files anywhere on the page lights up the drop box, and a drop anywhere publishes
  // (like Netlify Drop / Tiiny.host). Only file drags count, never text or links.
  useEffect(() => {
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types || []).includes("Files");
    const enter = (e: DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); depth.current += 1; setOver(true); };
    const overFn = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const leave = (e: DragEvent) => { if (!hasFiles(e)) return; depth.current -= 1; if (depth.current <= 0) { depth.current = 0; setOver(false); } };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setOver(false);
      const dt = e.dataTransfer;
      if (dt) take(() => readDrop(dt));
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragover", overFn);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragover", overFn);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", drop);
    };
  });

  const liveCount = rows.filter((r) => r.step === "live").length;
  const total = rows.length;
  const barColor = (st: Step) =>
    st === "live" ? "bg-emerald-500" : st === "failed" ? "bg-red-400" : st === "kept" ? "bg-gray-300" : st === "waiting" ? "bg-amber-400" : "bg-blue-500";
  const textColor = (st: Step) =>
    st === "live" ? "font-medium text-emerald-600" : st === "failed" ? "text-red-600" : st === "waiting" ? "text-amber-600" : "text-gray-500 dark:text-white/50";

  return (
    <div className="mb-8">
      <style>{`@keyframes sites-pulse{0%{box-shadow:0 0 0 0 rgba(59,130,246,.28)}100%{box-shadow:0 0 0 18px rgba(59,130,246,0)}}`}</style>
      <input
        ref={input}
        type="file"
        accept=".html,.htm,text/html"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = "";
          take(() => readFiles(files));
        }}
      />
      <div
        onClick={() => !running && !over && input.current?.click()}
        className={`group/dz cursor-pointer rounded-2xl border-2 border-dashed px-8 py-14 text-center transition-all duration-200 ${
          over
            ? "scale-[1.01] border-blue-500 bg-blue-50 dark:bg-blue-500/10"
            : "border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/40 dark:border-white/15 dark:bg-white/[0.02] dark:hover:border-blue-400/60"
        } ${running ? "pointer-events-none opacity-60" : ""}`}
        style={over ? { animation: "sites-pulse 1.2s infinite" } : undefined}
      >
        <div
          className={`mx-auto flex h-16 w-16 items-center justify-center rounded-2xl transition-all duration-200 ${
            over ? "-translate-y-1.5 scale-110 bg-blue-500 text-white shadow-lg" : "bg-gray-100 text-gray-400 group-hover/dz:-translate-y-0.5 group-hover/dz:bg-blue-100 group-hover/dz:text-blue-600 dark:bg-white/5 dark:text-white/40"
          }`}
        >
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0l-4 4m4-4l4 4M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3" />
          </svg>
        </div>
        <p className="mt-5 text-lg font-semibold text-gray-900 dark:text-white">
          {over ? "Drop to publish" : running ? "Publishing your pages…" : "Drop your pages here to put them online"}
        </p>
        <p className="mt-1.5 text-[15px] text-gray-500 dark:text-white/50">
          {over ? (
            "Let go and they'll be live in a few seconds"
          ) : (
            <>
              One page, several, or a whole folder — or{" "}
              <span className="font-medium text-blue-600">browse</span>
              {" · "}
              <button
                type="button"
                className="font-medium text-blue-600 hover:underline"
                onClick={(e) => { e.stopPropagation(); onPaste(); }}
              >
                paste code
              </button>
            </>
          )}
        </p>
        {siteAddress && !over && (
          <p className="mt-4 text-[13px] text-gray-400 dark:text-white/30">
            Pages go live at <span className="font-medium text-gray-500 dark:text-white/50">{siteAddress}/…</span> · we'll ask before replacing anything that's already live
          </p>
        )}
      </div>

      {(rows.length > 0 || skipped.length > 0) && (
        <div className="mt-6 space-y-3">
          {rows.length > 0 && (
            <div className="flex items-center justify-between px-1">
              <p className="text-[15px] font-semibold text-gray-900 dark:text-white">
                {running ? `Publishing ${total} page${total === 1 ? "" : "s"}…` : liveCount ? `${liveCount} page${liveCount === 1 ? " is" : "s are"} live 🎉` : "Nothing went live"}
              </p>
              <div className="flex items-center gap-4 text-[13px]">
                <span className="text-gray-400">{liveCount} of {total} live</span>
                {!running && (
                  <button type="button" className="text-gray-400 hover:text-gray-700 dark:hover:text-white" onClick={() => { setRows([]); setSkipped([]); }}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
          {rows.map((r) => (
            <div key={r.key} className="flex items-center gap-4 rounded-xl bg-white px-5 py-4 shadow-sm ring-1 ring-black/[0.05] transition-all duration-200 dark:bg-white/5 dark:ring-white/10">
              <div className={`flex h-10 w-10 flex-none items-center justify-center rounded-xl ${r.step === "live" ? "bg-emerald-50 text-emerald-600" : r.step === "failed" ? "bg-red-50 text-red-500" : "bg-blue-50 text-blue-600"} dark:bg-white/5`}>
                {r.step === "live" ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6M7 4h7l5 5v11a1 1 0 01-1 1H7a1 1 0 01-1-1V5a1 1 0 011-1z" /></svg>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-3">
                  <p className="truncate text-[15px] font-semibold text-gray-900 dark:text-white">{r.name}</p>
                  <span className={`flex-none text-[13px] ${textColor(r.step)}`}>
                    {STEP_TEXT[r.step]}{r.step === "live" ? " ✓" : ""}
                  </span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
                  <div className={`h-full rounded-full transition-all duration-500 ${barColor(r.step)}`} style={{ width: `${r.pct}%` }} />
                </div>
                {(r.url || r.note) && (
                  <p className="mt-2 truncate text-[13px] text-gray-500 dark:text-white/50">
                    {r.url && r.step === "live" && (
                      <>
                        <a href={r.url} target="_blank" rel="noreferrer" className="font-medium text-blue-600 hover:underline">{r.url.replace(/^https?:\/\//, "")}</a>
                        <button
                          type="button"
                          className="ml-3 rounded-md px-2 py-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-white/10"
                          onClick={(e) => { navigator.clipboard?.writeText(r.url || ""); const b = e.currentTarget; b.textContent = "Copied ✓"; setTimeout(() => (b.textContent = "Copy link"), 1500); }}
                        >
                          Copy link
                        </button>
                        <a href={r.url} target="_blank" rel="noreferrer" className="rounded-md px-2 py-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-white/10">Open</a>
                        {r.note ? <span className="ml-2">{r.note}</span> : null}
                      </>
                    )}
                    {!(r.url && r.step === "live") && r.note}
                  </p>
                )}
              </div>
            </div>
          ))}
          {skipped.length > 0 && (
            <p className="px-1 text-[13px] text-gray-400">
              Skipped {skipped.length} file{skipped.length === 1 ? "" : "s"} that {skipped.length === 1 ? "isn't a page" : "aren't pages"} ({skipped.slice(0, 3).join(", ")}{skipped.length > 3 ? "…" : ""}). Images and other site files are coming soon.
            </p>
          )}
        </div>
      )}

      {undo && (
        <div className="fixed bottom-6 left-1/2 z-[120] -translate-x-1/2">
          <div className="flex items-center gap-3 rounded-xl bg-gray-900 px-5 py-3 text-sm text-white shadow-xl ring-1 ring-black/[0.2]">
            <span>{undo.rows.length} page{undo.rows.length === 1 ? "" : "s"} published</span>
            <span className="text-gray-600">·</span>
            <button type="button" disabled={undoing} className="font-semibold text-white hover:text-gray-200" onClick={undoBatch}>{undoing ? "Undoing…" : "Undo"}</button>
          </div>
        </div>
      )}

      {confirm && (
        <Modal
          title="Replace a live page?"
          onClose={() => { confirm.resolve(false); setConfirm(null); }}
          onSuccess={() => { confirm.resolve(true); setConfirm(null); }}
          successCaption="Replace it"
          size={PopupSizes.SMALL}
        >
          <p className="text-left text-sm text-black/70 dark:text-white/70">{confirm.text}</p>
        </Modal>
      )}
    </div>
  );
};
